/**
 * useGoogleSignIn — wrapper alrededor de Google Identity Services (GIS).
 *
 * Estrategia (paquete AB):
 *   - Carga el script `https://accounts.google.com/gsi/client` ON-DEMAND
 *     la primera vez que se monta el botón. Esto evita inflar el bundle
 *     base (~50KB) cuando el usuario está navegando autenticado.
 *   - El client ID viene de `VITE_GOOGLE_CLIENT_ID`. Si está vacío (mock,
 *     QA, CI) el hook entra en MODO MOCK y devuelve un idToken fabricado
 *     con payload base64 para que el endpoint mock pueda decodificarlo.
 *   - Una vez cargado GIS, el hook expone `prompt()` que dispara el flujo
 *     One Tap / popup. El callback recibe el `credential` (JWT) que se le
 *     pasa al backend (`loginConGoogle`).
 *
 * En producción:
 *   1. Configurar `VITE_GOOGLE_CLIENT_ID` con el OAuth Client ID del
 *      Google Cloud project de Everex (tipo Web Application).
 *   2. En Google Cloud, autorizar el origin de la app (https://riga-traza...).
 *   3. Configurar `hd` (hosted domain) en el flow → solo @everexpharma.com.
 *   4. El backend valida el idToken con google-auth-library (verifyIdToken).
 *
 * Limitación conocida: GIS solo funciona en HTTPS o localhost. En IP de LAN
 * (192.168.x.x) hay que crear un cert local. Documentado en Handoff.
 */

import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            // Hosted domain restriction — solo acepta cuentas de este dominio.
            hd?: string;
            // UX: si el user ya tiene sesión Google activa, propone login directo.
            auto_select?: boolean;
            // Cancela el flow si el user clickea afuera del modal.
            cancel_on_tap_outside?: boolean;
          }): void;
          renderButton(parent: HTMLElement, config: {
            type?: 'standard' | 'icon';
            theme?: 'outline' | 'filled_blue' | 'filled_black';
            size?: 'large' | 'medium' | 'small';
            text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
            shape?: 'rectangular' | 'pill' | 'circle' | 'square';
            logo_alignment?: 'left' | 'center';
            width?: string | number;
            locale?: string;
          }): void;
          prompt(notificationCb?: (notification: unknown) => void): void;
          disableAutoSelect(): void;
        };
      };
    };
  }
}

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/** Promise cache para que el script se cargue una sola vez aunque se monten N botones. */
let gisScriptPromise: Promise<void> | null = null;

function loadGisScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisScriptPromise) return gisScriptPromise;
  gisScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Falló la carga de Google Identity Services')),
        { once: true },
      );
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Falló la carga de Google Identity Services'));
    document.head.appendChild(s);
  });
  return gisScriptPromise;
}

/**
 * Genera un idToken FAKE para el modo mock (sin VITE_GOOGLE_CLIENT_ID).
 * Es un base64url del payload — el endpoint mock lo decodifica con la
 * misma lógica que un JWT real (segmento del medio del JWT).
 */
function buildMockIdToken(email: string, name: string): string {
  const payload = {
    sub: `mock-${email}`,
    email,
    email_verified: true,
    hd: email.split('@')[1] ?? '',
    name,
    picture: undefined,
  };
  const b64 = btoa(JSON.stringify(payload))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
  // Devolvemos como JWT-shape (header.payload.signature) para parecer real.
  // Header y signature son dummy — el mock solo lee el payload.
  return `mockheader.${b64}.mocksig`;
}

export interface UseGoogleSignInResult {
  /** Cuando true, podemos llamar a renderButton/promptOneTap. */
  ready: boolean;
  /** Error de inicialización (CSP, network, GIS down, etc). */
  error: string | null;
  /** Si true, GIS NO está configurado — usar el botón mock. */
  modoMock: boolean;
  /** Renderizar el botón oficial de Google en un container DOM. */
  renderButton: (container: HTMLElement, onSuccess: (idToken: string) => void) => void;
  /** Disparar el flow mock (sin GIS configurado) con un email arbitrario. */
  signInMock: (email: string, nombre: string) => string;
}

export function useGoogleSignIn(): UseGoogleSignInResult {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const modoMock = !clientId || clientId.trim() === '';

  const [ready, setReady] = useState<boolean>(modoMock);
  const [error, setError] = useState<string | null>(null);
  const onSuccessRef = useRef<((idToken: string) => void) | null>(null);

  useEffect(() => {
    if (modoMock) return;
    let cancelled = false;
    loadGisScript()
      .then(() => {
        if (cancelled) return;
        if (!window.google) {
          setError('GIS cargó pero `window.google` no está definido');
          return;
        }
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            onSuccessRef.current?.(response.credential);
          },
          hd: 'everexpharma.com',
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        setReady(true);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, modoMock]);

  const renderButton = (container: HTMLElement, onSuccess: (idToken: string) => void) => {
    onSuccessRef.current = onSuccess;
    if (modoMock || !window.google) return;
    window.google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 320,
      locale: 'es',
    });
  };

  const signInMock = (email: string, nombre: string): string => {
    return buildMockIdToken(email, nombre);
  };

  return { ready, error, modoMock, renderButton, signInMock };
}
